import { WAVE, ENEMY_BASE, ENEMY_SPAWN_X, ENEMY_MIN_Y, ENEMY_MAX_Y, ENEMY_TYPES, SCALING, GUARANTEED_SPAWNS, BOSS_DEFINITIONS } from '../config';
import { WaveConfig, EnemyConfig, EnemyType } from '../types';

export class WaveManager {
  currentWave: number;
  enemiesRemaining: number;
  enemiesSpawned: number;
  totalEnemiesInWave: number;
  isWaveActive: boolean;
  isBossWave: boolean = false;
  bossAlive: boolean = false;
  public waveJustCompleted: boolean = false;
  private spawnTimer: number;
  private spawnInterval: number;
  private currentWaveConfig: WaveConfig | null;
  private waveEnemyTypes: EnemyType[] = [];
  private spawnQueue: EnemyType[] = [];

  constructor() {
    this.currentWave = 0;
    this.enemiesRemaining = 0;
    this.enemiesSpawned = 0;
    this.totalEnemiesInWave = 0;
    this.isWaveActive = false;
    this.isBossWave = false;
    this.bossAlive = false;
    this.waveJustCompleted = false;
    this.spawnTimer = 0;
    this.spawnInterval = 0;
    this.currentWaveConfig = null;
  }

  getEnemyTypesForWave(wave: number): EnemyType[] {
    // Progressive introduction of enemy types
    const types: EnemyType[] = ['basic'];

    if (wave >= 3) {
      types.push('runner'); // Fast enemies from wave 3
    }
    if (wave >= 5) {
      types.push('brute'); // Tanky enemies from wave 5
      types.push('archer'); // Ranged enemies from wave 5
    }
    if (wave >= 8) {
      types.push('shaman'); // Healers from wave 8
    }
    if (wave >= 10) {
      types.push('shieldbearer'); // Shield + club from wave 10
    }
    if (wave >= 20) {
      types.push('longbow_archer'); // Longbow archers from wave 20
      types.push('saboteur'); // Burrower targeting wall directly
    }
    if (wave >= 25) {
      types.push('swarmling'); // Tiny fast swarm from wave 25
    }
    if (wave >= 30) {
      types.push('war_drummer'); // Buffs speeds of nearby enemies
    }
    if (wave >= 35) {
      types.push('splitter'); // Splits into 2 on death from wave 35
    }
    if (wave >= 45) {
      types.push('nest_carrier'); // Spawns swarmlings while alive from wave 45
    }
    if (wave >= 50) {
      types.push('siege_tower'); // Slow deployer block
    }
    if (wave >= 55) {
      types.push('enemy_trebuchet'); // Siege — attacks wall directly from wave 55
    }
    // boulder spawns separately via GameScene's independent timer — not in the regular pool
    // loot_goblin spawns sporadically everywhere, not in standard generation list.

    return types;
  }

  selectEnemyType(availableTypes: EnemyType[]): EnemyType {
    // Weighted selection - basic enemies are more common
    const weights: Record<EnemyType, number> = {
      basic: 50,
      runner: 25,
      brute: 15,
      archer: 15,
      shaman: 10,
      shieldbearer: 20,
      longbow_archer: 12,
      swarmling: 25,
      splitter: 16,
      nest_carrier: 14,
      enemy_trebuchet: 12,
      boulder: 0,
      saboteur: 18,
      war_drummer: 8,
      loot_goblin: 0, // Injected manually outside standard weights
      siege_tower: 6, // Ramping threat
    };

    let totalWeight = 0;
    for (const type of availableTypes) {
      totalWeight += weights[type];
    }

    let random = Math.random() * totalWeight;
    for (const type of availableTypes) {
      random -= weights[type];
      if (random <= 0) {
        return type;
      }
    }

    return 'basic';
  }

  /** Exponential enemy stat calculation */
  private getBaseStats(wave: number): { hp: number; damage: number; speed: number; gold: number } {
    return {
      hp: Math.round(SCALING.enemyBaseHp * Math.pow(1 + SCALING.enemyHpGrowth, wave)),
      damage: Math.round(SCALING.enemyBaseDmg * Math.pow(1 + SCALING.enemyDmgGrowth, wave)),
      speed: Math.min(ENEMY_BASE.speed + wave * ENEMY_BASE.speedPerWave, ENEMY_BASE.maxSpeed),
      gold: Math.round(SCALING.enemyBaseGold * Math.pow(1 + SCALING.enemyGoldGrowth, wave)),
    };
  }

  getWaveConfig(wave: number): WaveConfig {
    const isBoss = wave % 5 === 0 && wave > 0;
    this.isBossWave = isBoss;

    const stats = this.getBaseStats(wave);

    let enemyCount: number;

    if (isBoss) {
      const minionCount = 5 + Math.floor(wave / 2) + Math.floor(wave / 5) * 2;
      enemyCount = minionCount + 1;
    } else {
      enemyCount = WAVE.baseEnemyCount + Math.floor(wave * WAVE.enemiesPerWave) + Math.floor(wave / 10) * WAVE.bonusPerMilestone;
    }

    const enemyConfig: EnemyConfig = {
      hp: stats.hp,
      speed: stats.speed,
      damage: stats.damage,
      attackInterval: ENEMY_BASE.attackInterval,
      goldReward: stats.gold,
      type: 'basic',
    };

    const spawnInterval = isBoss
      ? 1154
      : Math.max(WAVE.minSpawnInterval, Math.round(WAVE.baseSpawnInterval * Math.pow(WAVE.spawnDecayPerWave, wave)));

    const enemyTypes = this.getEnemyTypesForWave(wave);

    return { enemyCount, enemyConfig, spawnInterval, enemyTypes };
  }

  getTypedEnemyConfig(baseConfig: EnemyConfig, type: EnemyType): EnemyConfig {
    // For boss waves, minion config is passed in.
    // We only use special logic if it's the actual boss unit (handled in update)
    // Here we just return standard typed config for minions.

    const multipliers = ENEMY_TYPES[type] || ENEMY_TYPES['basic'];

    const config: EnemyConfig = {
      hp: Math.round(baseConfig.hp * multipliers.hpMult),
      speed: baseConfig.speed * multipliers.speedMult,
      damage: Math.round(baseConfig.damage * multipliers.damageMult),
      attackInterval: type === 'enemy_trebuchet' ? Math.round(baseConfig.attackInterval * 2.5) : baseConfig.attackInterval,
      goldReward: Math.round(baseConfig.goldReward * multipliers.goldMult),
      type: type,
      attackRange: multipliers.attackRange || 0,
    };

    // Add shaman healing percent
    if (type === 'shaman' && 'healPercent' in multipliers) {
      config.healPercent = (multipliers as any).healPercent;
    }

    // Add miss chance for ranged enemies
    if ('missChance' in multipliers) {
      config.missChance = (multipliers as any).missChance;
    }

    // Add ranged damage reduction for shieldbearers
    if ('rangedDamageReduction' in multipliers) {
      config.rangedDamageReduction = (multipliers as any).rangedDamageReduction;
    }

    // Add wall-damage-only flag for enemy trebuchets
    if ('wallDamageOnly' in multipliers) {
      config.wallDamageOnly = (multipliers as any).wallDamageOnly;
    }

    return config;
  }

  private createBossConfig(wave: number): EnemyConfig {
    const bossIndex = (Math.floor(wave / 5) - 1) % BOSS_DEFINITIONS.length;
    const def = BOSS_DEFINITIONS[bossIndex];
    const stats = this.getBaseStats(wave);

    return {
      hp: stats.hp * def.hpMult,
      speed: ENEMY_BASE.speed * 0.55,
      damage: Math.round(stats.damage * def.dmgMult),
      attackInterval: ENEMY_BASE.attackInterval * 0.8,
      goldReward: Math.round(stats.gold * WAVE.bossGoldMultiplier),
      type: def.baseType as EnemyType,
      isBoss: true,
      scale: def.scale,
      attackRange: def.attackRange,
      missChance: 0.1,
      bossAbility: def.ability,
      abilityDescription: def.abilityDescription,
    };
  }

  /** Returns guaranteed spawn entries active for the given wave. */
  private getGuaranteedSpawnsForWave(wave: number): { type: EnemyType; count: number }[] {
    return GUARANTEED_SPAWNS.filter(g => wave >= g.introWave);
  }

  /**
   * Builds a shuffled spawn queue for a wave.
   * `nonBossCount` is the number of non-boss enemies to spawn.
   * Guaranteed enemies are added on top, then the remainder fills the random pool.
   * Returns the queue (length = nonBossCount + total guaranteed count).
   */
  private buildSpawnQueue(nonBossCount: number, wave: number): EnemyType[] {
    const availableTypes = this.getEnemyTypesForWave(wave);
    const guaranteed = this.getGuaranteedSpawnsForWave(wave);

    const queue: EnemyType[] = [];

    // Add guaranteed entries
    for (const { type, count } of guaranteed) {
      for (let i = 0; i < count; i++) {
        queue.push(type);
      }
    }

    // Fill the rest with random pool selections
    for (let i = 0; i < nonBossCount; i++) {
      queue.push(this.selectEnemyType(availableTypes));
    }

    // Fisher-Yates shuffle
    for (let i = queue.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [queue[i], queue[j]] = [queue[j], queue[i]];
    }

    return queue;
  }

  startWave(): WaveConfig {
    this.currentWave++;
    this.currentWaveConfig = this.getWaveConfig(this.currentWave);
    this.isWaveActive = true;
    this.bossAlive = false;
    this.spawnInterval = this.currentWaveConfig.spawnInterval;
    this.spawnTimer = 0;
    this.waveEnemyTypes = this.currentWaveConfig.enemyTypes || ['basic'];

    const isBoss = this.isBossWave;
    // For boss waves, base count excludes the boss itself (last slot is always the boss)
    const nonBossCount = isBoss ? this.currentWaveConfig.enemyCount - 1 : this.currentWaveConfig.enemyCount;

    this.spawnQueue = this.buildSpawnQueue(nonBossCount, this.currentWave);

    // Total includes the guaranteed extras + boss (if boss wave)
    this.totalEnemiesInWave = this.spawnQueue.length + (isBoss ? 1 : 0);
    this.enemiesRemaining = this.totalEnemiesInWave;
    this.enemiesSpawned = 0;

    return this.currentWaveConfig;
  }

  update(delta: number): { shouldSpawn: boolean; config: EnemyConfig | null; spawnX: number; spawnY: number; isBoss: boolean } {
    if (!this.isWaveActive || !this.currentWaveConfig) {
      return { shouldSpawn: false, config: null, spawnX: 0, spawnY: 0, isBoss: false };
    }

    // If normal wave, stop when limit reached
    // If boss wave AND boss is NOT alive yet, standard logic applies until boss spawns
    // Once boss is alive, ignore limit and keep spawning minions
    if (!this.bossAlive && this.enemiesSpawned >= this.totalEnemiesInWave) {
      return { shouldSpawn: false, config: null, spawnX: 0, spawnY: 0, isBoss: false };
    }

    this.spawnTimer -= delta;
    if (this.spawnTimer <= 0) {
      this.spawnTimer = this.spawnInterval; // Use longer interval for subsequent
      this.enemiesSpawned++;

      const spawnY = ENEMY_MIN_Y + Math.random() * (ENEMY_MAX_Y - ENEMY_MIN_Y);
      let enemyConfig: EnemyConfig;
      let isBossSpawn = false;

      // Select enemy type
      if (!this.bossAlive && this.isBossWave && this.enemiesSpawned === this.totalEnemiesInWave) {
        // Last enemy of the initial count is the BOSS
        enemyConfig = this.createBossConfig(this.currentWave);
        isBossSpawn = true;
        this.bossAlive = true;
      } else if (this.bossAlive && this.isBossWave) {
        // Post-boss infinite minions — random pool (no queue)
        const enemyType = this.selectEnemyType(this.waveEnemyTypes);
        enemyConfig = this.getTypedEnemyConfig(this.currentWaveConfig.enemyConfig, enemyType);
      } else {
        // Normal wave or pre-boss minions — read from pre-built queue
        const queueIndex = this.enemiesSpawned - 1;
        const enemyType = this.spawnQueue[queueIndex] ?? this.selectEnemyType(this.waveEnemyTypes);
        enemyConfig = this.getTypedEnemyConfig(this.currentWaveConfig.enemyConfig, enemyType);
      }

      return {
        shouldSpawn: true,
        config: enemyConfig,
        spawnX: ENEMY_SPAWN_X,
        spawnY,
        isBoss: isBossSpawn,
      };
    }

    return { shouldSpawn: false, config: null, spawnX: 0, spawnY: 0, isBoss: false };
  }

  onEnemyKilled(isBoss: boolean = false): void {
    if (isBoss) {
      this.bossAlive = false;
    }
    this.enemiesRemaining--;

    // Wave completes if no enemies left AND boss is dead (or not spawned/not boss wave)
    if (this.isWaveActive && this.enemiesRemaining <= 0 && !this.bossAlive) {
      this.isWaveActive = false;
      this.waveJustCompleted = true;
    }
  }

  isWaveComplete(): boolean {
    if (this.waveJustCompleted) {
      this.waveJustCompleted = false; // consume the flag
      return true;
    }
    return false;
  }

  getRestartWave(): number {
    return Math.max(0, this.currentWave - WAVE.defeatRollback);
  }

  setWave(wave: number): void {
    this.currentWave = wave;
    this.isWaveActive = false;
    this.isBossWave = false;
    this.bossAlive = false;
    this.enemiesRemaining = 0;
    this.waveJustCompleted = false;
  }

  reset(): void {
    this.currentWave = 0;
    this.isWaveActive = false;
    this.isBossWave = false;
    this.bossAlive = false;
    this.enemiesRemaining = 0;
    this.enemiesSpawned = 0;
    this.totalEnemiesInWave = 0;
    this.waveJustCompleted = false;
    this.spawnTimer = 0;
    this.spawnInterval = 0;
    this.currentWaveConfig = null;
    this.waveEnemyTypes = [];
    this.spawnQueue = [];
  }
}
